import { useState } from "react";
import { Users } from "lucide-react";
import { Card, CardContent } from "../../ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../ui/tabs";
import { TopResourceActivityProps } from "./interfaces/interface";
import { Skeleton } from "../interfaces/CommonInterfaces";
import { cn } from "../../../utils/utils";
import OverviewCardHeader from "./OverviewCardHeader";
import OverviewEmptyState from "./OverviewEmptyState";

export default function TopResourceActivity({
  fromdetails = false,
  cardLayout = fromdetails,
  topUsers,
  topResources,
  topComputers,
  loading,
}: TopResourceActivityProps) {
  const [activeSubTab, setActiveSubTab] = useState("users");
  const rankByActivity = <T extends { count: number }>(items?: T[]) =>
    fromdetails
      ? items?.filter((item) => Number(item.count) > 0).sort((a, b) => Number(b.count) - Number(a.count))
      : items;
  const groups = [
    ...(!fromdetails ? [{
      id: "computers",
      title: "Top Computers",
      testId: "tab-top-users",
      rowPrefix: "list-user",
      items: topComputers?.map((item) => ({ ...item, label: item.name })),
    }] : []),
    {
      id: "users",
      title: "Top Users",
      testId: "tab-top-users",
      rowPrefix: "list-user",
      items: rankByActivity(topUsers)?.map((item) => ({ ...item, label: item.user })),
    },
    {
      id: "resources",
      title: "Top Resources",
      testId: "tab-top-resources",
      rowPrefix: "list-resource",
      items: rankByActivity(topResources)?.map((item) => ({ ...item, label: item.resource })),
    },
  ];

  return (
    <Card className="overview-card overview-ranking-card flex-shrink-0">
      {cardLayout && (
        <OverviewCardHeader
          fromdetails={cardLayout}
          title="Top activity"
          description={fromdetails ? "Most active users and resources on this endpoint" : "Most active computers, users and resources across your endpoints"}
          icon={Users}
        />
      )}
      <CardContent className="overview-card-content overview-ranking-content pt-6">
        <Tabs value={activeSubTab} onValueChange={setActiveSubTab}>
          <TabsList
            unstyled={cardLayout}
            className={cn("overview-ranking-tabs grid w-full", !fromdetails ? "grid-cols-3" : "grid-cols-2")}
          >
            {groups.map((group) => (
              <TabsTrigger
                key={group.id}
                unstyled={cardLayout}
                value={group.id}
                data-testid={group.testId}
                className={cardLayout ? "overview-ranking-tab" : "rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:font-medium"}
              >
                {group.title}
              </TabsTrigger>
            ))}
          </TabsList>
          {groups.map((group) => (
            <TabsContent key={group.id} value={group.id} className="mt-4">
              <div className="overview-ranking-list space-y-3">
                {loading ? (
                  Array.from({ length: 5 }, (_, i) => (
                    <div key={i} className="overview-ranking-row flex items-center justify-between gap-4 py-1.5">
                      <div className="flex items-start flex-1 min-w-0 gap-2">
                        <Skeleton className="w-4 h-4 rounded" />
                        <Skeleton className="w-32 h-4" />
                      </div>
                      <div className="overview-ranking-stats flex items-center gap-2 min-w-[120px]">
                        <Skeleton className="h-2 w-[100px] rounded-full" />
                        <Skeleton className="w-8 h-4" />
                      </div>
                    </div>
                  ))
                ) : group.items && group.items.length > 0 ? (
                  group.items.map((item, index) => (
                    <div
                      key={item.id}
                      className="overview-ranking-row flex items-center justify-between gap-4 py-1.5 cursor-pointer"
                      data-testid={`${group.rowPrefix}-${item.id}`}
                      title={`Count: ${item.count}`}
                    >
                      <div className="overview-ranking-identity flex items-start flex-1 min-w-0 gap-2">
                        <span className="overview-rank flex-shrink-0 text-sm font-medium">{cardLayout ? index + 1 : `${index + 1}.`}</span>
                        <div className="min-w-0">
                          <span className="overview-ranking-name text-sm break-words text-foreground">{item.label}</span>
                          {cardLayout && <span className="overview-ranking-count">{item.count.toLocaleString()} events</span>}
                        </div>
                      </div>
                      <div className="overview-ranking-stats flex items-center gap-2 min-w-[120px]">
                        <div className="overview-ranking-track w-full max-w-[100px] h-2 overflow-hidden rounded-full bg-muted">
                          <div className="overview-ranking-fill h-full rounded-full bg-primary" style={{ width: `${item.percent}%` }} />
                        </div>
                        <span className="overview-ranking-percent w-12 text-xs text-right text-muted-foreground">{item.percent || 0}%</span>
                      </div>
                    </div>
                  ))
                ) : cardLayout ? (
                  <OverviewEmptyState icon={Users} message={`No ${group.id === "users" ? "user" : group.id === "computers" ? "computer" : "resource"} activity found in this time range.`} />
                ) : (
                  <div className="py-6 text-sm text-center text-muted-foreground">No data found.</div>
                )}
              </div>
            </TabsContent>
          ))}
        </Tabs>
      </CardContent>
    </Card>
  );
}
